import { useCallback, useContext, useEffect, useRef, useState } from "react";
import {
  DockviewDefaultTab,
  DockviewReact,
  type DockviewApi,
  type DockviewGroupPanel,
  type DockviewReadyEvent,
  type IDockviewHeaderActionsProps,
  type IDockviewPanel,
  type IDockviewPanelHeaderProps,
  type SerializedDockview,
} from "dockview-react";
import "dockview/dist/styles/dockview.css";
import {
  activeWorkspace,
  migrateLayoutOnRead,
  useLinkGroups,
  type LinkGroup,
  type LinkTarget,
  type SerializedLayout,
} from "@helm/core";
import { PanelFrame } from "./panels";
import { ChromeContext, ModuleMenu, syncChartHeaders, useTradovateStyle, type Chrome } from "./panelChrome";
import {
  DEFAULT_PAD_MODE,
  MODULE_BY_KIND,
  TABLE_KINDS,
  TEMPLATES,
  WIDGET_TITLES,
  type WidgetKind,
  type WidgetParams,
  type WorkspaceTemplate,
} from "./templates";
import { useWorkspaces, type WorkspaceStore } from "./workspaceStore";
import {
  chartOnlyApplied,
  dockChartCount,
  leaveChartOnlyFor,
  onChartOnlyChange,
  setChartOnly,
  setChartOnlyCapture,
  syncChartOnly,
  useChartOnly,
} from "./chartOnly";
import { pushToast } from "../components/Toasts";
import { Icon } from "../components/Icon";

type ChartSeedModule = typeof import("../chart/chartTemplatesDoc");
let chartSeed: ChartSeedModule | null = null;

/**
 * A new chart module starts from the user's default chart template (owner 2026-09-28), if any. The
 * template document is sanitised against the indicator registry, which loads with the chart chunks
 * (audit S-12 budget): the panel is added once it is in (at once after the first time). If it cannot
 * load, the chart is added without the template; the chart's own chunk then offers Retry.
 */
function withChartSeed(add: (seed: Record<string, unknown>) => void): void {
  if (chartSeed !== null) {
    add(chartSeed.defaultChartTemplateSeed());
    return;
  }
  import("../chart/chartTemplatesDoc").then(
    (m) => {
      chartSeed = m;
      add(m.defaultChartTemplateSeed());
    },
    (e: unknown) => {
      console.error("[lazy] chart templates failed", e);
      add({});
    },
  );
}

function paramsFor(kind: WidgetKind, link: LinkTarget, mode?: WidgetParams["mode"]): WidgetParams {
  const m = mode ?? DEFAULT_PAD_MODE[kind];
  return m ? { kind, link, mode: m } : { kind, link };
}

function applyTemplateTo(api: DockviewApi, t: WorkspaceTemplate): void {
  api.clear();
  // Multi-symbol templates start their extra link groups on a symbol, unless the user picked one.
  const groups = useLinkGroups.getState();
  for (const [g, sym] of Object.entries(t.linkSymbols ?? {}) as [LinkGroup, string][]) {
    if (!groups.groups[g].symbol) groups.setSymbol(g, sym);
  }
  for (const p of t.panels) {
    api.addPanel({
      id: p.id,
      component: "widget",
      title: WIDGET_TITLES[p.kind],
      params: {
        ...paramsFor(p.kind, p.link, p.mode),
        ...(p.tf !== undefined ? { tf: p.tf } : {}),
        ...(p.overlays !== undefined ? { overlays: p.overlays } : {}),
        ...(p.rail !== undefined ? { rail: p.rail } : {}),
        ...p.params,
      },
      ...(p.position
        ? { position: { referencePanel: p.position.referencePanel, direction: p.position.direction } }
        : {}),
      ...(p.size !== undefined ? { initialWidth: p.size, initialHeight: p.size } : {}),
    });
  }
  // initialWidth/Height only seed a split; neighbours added later take space from it. Pin the
  // template sizes once everything is placed.
  // Splitview resizes take space from the pinned panels' neighbours but never from the anchor, so
  // shrink the anchor to what its directly attached panels leave, then pin those panels; a few
  // passes settle every pinned panel at its size.
  const anchor = t.panels[0];
  if (anchor && api.width > 0 && api.height > 0) {
    const attached = t.panels.filter((p) => p.position?.referencePanel === anchor.id && p.size !== undefined);
    const sum = (dirs: readonly string[]): number =>
      attached
        .filter((p) => dirs.includes(p.position?.direction ?? ""))
        .reduce((s, p) => s + (p.size ?? 0), 0);
    // An anchor with its own `size` states its width (a row with panels attached further out,
    // e.g. watchlist | 1D chart | anchor, cannot be derived from the directly attached ones).
    const w = anchor.size ?? api.width - sum(["left", "right"]);
    const h = api.height - sum(["above", "below"]);
    const a = api.getPanel(anchor.id)?.api;
    if (w > 200) a?.setSize({ width: w });
    if (h > 200) a?.setSize({ height: h });
  }
  for (let pass = 0; pass < 3; pass++) {
    for (const p of t.panels) {
      if (p.size === undefined || !p.position || p.pin === false) continue;
      const horizontal = p.position.direction === "left" || p.position.direction === "right";
      api.getPanel(p.id)?.api.setSize(horizontal ? { width: p.size } : { height: p.size });
    }
  }
  // Tabbed groups show their first panel (e.g. Positions, not the last-added Notifications).
  const heads = new Set(
    t.panels.flatMap((p) => (p.position?.direction === "within" ? [p.position.referencePanel] : [])),
  );
  for (const id of heads) api.getPanel(id)?.api.setActive();
  const first = t.panels[0];
  if (first) api.getPanel(first.id)?.api.setActive();
}

function templateOf(id: string): WorkspaceTemplate | undefined {
  return TEMPLATES.find((t) => t.id === id) ?? TEMPLATES[0];
}

const kindOf = (p: IDockviewPanel): WidgetKind | undefined => (p.params as WidgetParams | undefined)?.kind;

function newPanelId(kind: WidgetKind): string {
  return `${kind}-${crypto.randomUUID().slice(0, 6)}`;
}

// ---------------------------------------------------------------------------------------------
// Module actions (add-module menu, palette, tab menu)

/** Where a new module goes: tables join the table group as a tab; others split off the active module. */
function placementFor(
  api: DockviewApi,
  kind: WidgetKind,
): Parameters<DockviewApi["addPanel"]>[0]["position"] {
  if (TABLE_KINDS.has(kind)) {
    const table = api.panels.find((p) => {
      const k = kindOf(p);
      return k !== undefined && TABLE_KINDS.has(k) && p.api.location.type === "grid";
    });
    return table ? { referencePanel: table.id, direction: "within" } : { direction: "below" };
  }
  const active = api.activePanel;
  const ref =
    active && !TABLE_KINDS.has(kindOf(active) ?? "positions") && active.api.location.type === "grid"
      ? active
      : api.panels.find((p) => !TABLE_KINDS.has(kindOf(p) ?? "positions") && p.api.location.type === "grid");
  return ref ? { referencePanel: ref.id, direction: "right" } : undefined;
}

export const workspaceActions = {
  /** Add a module (multiple instances allowed except `single` ones, which are focused). */
  addModule(api: DockviewApi, kind: WidgetKind, link?: LinkTarget): void {
    // A non-chart module leaves Chart only first, so it shows where it is placed.
    leaveChartOnlyFor(kind);
    if (MODULE_BY_KIND.get(kind)?.single) {
      workspaceActions.focusOrAdd(api, kind);
      return;
    }
    const lk: LinkTarget = link ?? (TABLE_KINDS.has(kind) || kind === "risk" ? "unlinked" : "red");
    const add = (seed: Record<string, unknown>): void => {
      const position = placementFor(api, kind);
      api.addPanel({
        id: newPanelId(kind),
        component: "widget",
        title: WIDGET_TITLES[kind],
        params: { ...paramsFor(kind, lk), ...seed },
        ...(position ? { position } : {}),
      });
    };
    // A new chart starts from the user's default chart template (owner 2026-09-28), if any.
    if (kind === "chart") withChartSeed(add);
    else add({});
  },
  /** Kept for existing callers (palette): same as addModule. */
  addWidget(api: DockviewApi, kind: WidgetKind, link?: LinkTarget): void {
    workspaceActions.addModule(api, kind, link);
  },
  /** Focus the first panel of `kind`, or add one (journal, copy trader: one is enough). */
  focusOrAdd(api: DockviewApi, kind: WidgetKind, link: LinkTarget = "unlinked"): void {
    leaveChartOnlyFor(kind);
    const existing = api.panels.find((p) => kindOf(p) === kind);
    if (existing) {
      existing.api.setActive();
      return;
    }
    const position = placementFor(api, kind);
    api.addPanel({
      id: newPanelId(kind),
      component: "widget",
      title: WIDGET_TITLES[kind],
      params: paramsFor(kind, link),
      ...(position ? { position } : {}),
    });
  },
  /** A second copy of a module with the same settings (symbol link, timeframe, filter…). */
  duplicate(api: DockviewApi, panel: IDockviewPanel): void {
    const params = { ...(panel.params as WidgetParams) };
    api.addPanel({
      id: newPanelId(params.kind),
      component: "widget",
      title: panel.title ?? WIDGET_TITLES[params.kind],
      params,
      position:
        panel.api.location.type === "grid"
          ? { referencePanel: panel.id, direction: "right" }
          : { referencePanel: panel.id, direction: "within" },
    });
  },
  applyTemplate(api: DockviewApi, templateId: string): void {
    const t = TEMPLATES.find((x) => x.id === templateId);
    if (t) applyTemplateTo(api, t);
  },
};

/**
 * Close a module with an "Undo" toast. Only that panel goes; link groups, other panels and the
 * trading/market stores are untouched. Undo puts it back beside its old tab-mates (or where it
 * floated) with the same id and settings.
 */
type Direction = "left" | "right" | "above" | "below";

/**
 * The grid group `group` was split from, and on which side of it `group` sits — enough to put a
 * closed single-module group back where it was. The split sibling shares a whole edge with it, so
 * the neighbour whose shared edge best covers both sides wins (a long table below a pair of
 * charts touches only half its top edge).
 */
function neighbourOf(
  api: DockviewApi,
  group: DockviewGroupPanel,
): { id: string; direction: Direction } | null {
  const r = group.element.getBoundingClientRect();
  let best: { id: string; direction: Direction; overlap: number } | null = null;
  const near = (a: number, b: number): boolean => Math.abs(a - b) < 8;
  for (const g of api.groups) {
    if (g === group || g.api.location.type !== "grid") continue;
    const q = g.element.getBoundingClientRect();
    const v = Math.min(r.bottom, q.bottom) - Math.max(r.top, q.top);
    const h = Math.min(r.right, q.right) - Math.max(r.left, q.left);
    const fitV = v / Math.max(r.height, q.height);
    const fitH = h / Math.max(r.width, q.width);
    const cands: [boolean, Direction, number][] = [
      [v > 0 && near(q.right, r.left), "right", fitV],
      [v > 0 && near(q.left, r.right), "left", fitV],
      [h > 0 && near(q.bottom, r.top), "below", fitH],
      [h > 0 && near(q.top, r.bottom), "above", fitH],
    ];
    for (const [ok, direction, overlap] of cands)
      if (ok && (best === null || overlap > best.overlap)) best = { id: g.id, direction, overlap };
  }
  return best && { id: best.id, direction: best.direction };
}

export function closeWithUndo(api: DockviewApi, panel: IDockviewPanel, isCurrent: () => boolean): void {
  const group = panel.group;
  const siblings = group.panels.filter((p) => p.id !== panel.id);
  const index = group.panels.indexOf(panel);
  const where = group.api.location.type;
  const rect = group.element.getBoundingClientRect();
  const saved = {
    id: panel.id,
    title: panel.title ?? WIDGET_TITLES[kindOf(panel) ?? "chart"],
    params: { ...(panel.params as WidgetParams) },
    ref: siblings[0]?.id ?? null,
    index,
    floating: where === "floating",
    neighbour: siblings.length === 0 && where === "grid" ? neighbourOf(api, group) : null,
    width: Math.round(rect.width),
    height: Math.round(rect.height),
  };
  panel.api.close();
  pushToast(
    { tone: "info", title: `${saved.title} closed`, body: "Undo puts it back where it was." },
    {
      ttlMs: 8000,
      action: {
        label: "Undo",
        run: () => {
          if (!isCurrent() || api.getPanel(saved.id)) return;
          const base = { id: saved.id, component: "widget", title: saved.title, params: saved.params };
          const n = saved.neighbour;
          if (saved.ref !== null && api.getPanel(saved.ref)) {
            api.addPanel({
              ...base,
              position: { referencePanel: saved.ref, direction: "within", index: saved.index },
            });
          } else if (n !== null && api.getGroup(n.id)) {
            const horizontal = n.direction === "left" || n.direction === "right";
            api.addPanel({
              ...base,
              position: { referenceGroup: n.id, direction: n.direction },
              ...(horizontal ? { initialWidth: saved.width } : { initialHeight: saved.height }),
            });
          } else if (saved.floating) {
            api.addPanel({ ...base, floating: { width: saved.width, height: saved.height } });
          } else {
            const position = placementFor(api, saved.params.kind);
            api.addPanel({ ...base, ...(position ? { position } : {}) });
          }
        },
      },
    },
  );
}

// ---------------------------------------------------------------------------------------------
// Chrome: tab (close with undo, hidden when locked) and group header actions

function ModuleTab(props: IDockviewPanelHeaderProps): React.JSX.Element {
  const { locked, close } = useContext(ChromeContext);
  const kind = (props.params as WidgetParams | undefined)?.kind;
  const icon = kind ? MODULE_BY_KIND.get(kind)?.icon : undefined;
  return (
    <div className="module-tab" data-kind={kind}>
      {icon && <Icon name={icon} size={12} className="module-tab-icon" />}
      <DockviewDefaultTab
        {...props}
        hideClose={locked}
        closeActionOverride={() => {
          const panel = props.containerApi.getPanel(props.api.id);
          if (panel) close(props.containerApi, panel);
        }}
      />
    </div>
  );
}

/** Group header (module title-bar controls): one "⋯" menu — options, maximize, float, pop out. */
function GroupActions(props: IDockviewHeaderActionsProps): React.JSX.Element | null {
  if (!props.activePanel) return null;
  return (
    <div className="group-actions">
      <ModuleMenu group={props.group} containerApi={props.containerApi} panel={props.activePanel} />
    </div>
  );
}

function EmptyWorkspace(): React.JSX.Element {
  return (
    <div className="empty-state ws-empty">
      <div className="empty-title">Empty workspace</div>
      <div className="muted">
        Use “Add module” above (or Ctrl+K → “Add module”) to place a chart, DOM, positions…
      </div>
    </div>
  );
}

/**
 * Chart only with no chart in the workspace: one line and the way to add one (the add-module path),
 * instead of a blank area.
 */
function ChartOnlyEmpty({ onAdd }: { onAdd: () => void }): React.JSX.Element {
  return (
    <div className="chart-only-empty" role="status">
      <span>No chart in this workspace —</span>
      <button type="button" className="btn btn-sm" onClick={onAdd}>
        <Icon name="plus" size={11} /> Add chart
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// Pop-out windows: same JS context (dockview popout groups), so the md socket, the trading
// socket and the chart render worker are shared; the window only needs the theme mirrored.

function mirrorTheme(win: Window): void {
  try {
    const src = document.documentElement;
    const dst = win.document.documentElement;
    for (const a of ["data-theme", "data-style", "style", "lang"]) {
      const v = src.getAttribute(a);
      if (v === null) dst.removeAttribute(a);
      else dst.setAttribute(a, v);
    }
    win.document.body.classList.add("popout-body");
  } catch {
    /* window closing */
  }
}

// ---------------------------------------------------------------------------------------------

/**
 * Dockable workspace (SPEC §17.3; owner 2026-09-27 "moveable like Tradovate"): every module is a
 * dockview panel that can be dragged to any edge, tabbed, split, resized, maximized, floated,
 * popped out into its own window, or closed (with Undo). The layout of the active workspace tab
 * lives in the `WorkspaceStore` (local cache + synced `layout/desktop` document).
 */
export function Workspace({
  store,
  onApi,
  onPopoutWindow,
}: {
  store: WorkspaceStore;
  onApi: (api: DockviewApi | null) => void;
  /** Called for each pop-out window (hotkeys); returns a cleanup. */
  onPopoutWindow?: (win: Window) => () => void;
}): React.JSX.Element {
  const { doc, revision } = useWorkspaces(store);
  const ws = activeWorkspace(doc);
  const apiRef = useRef<DockviewApi | null>(null);
  const popoutCleanups = useRef(new Map<Window, () => void>());
  // Tradovate style: chart-only groups drop dockview's tab row (the chart bar carries the chrome).
  const tradovate = useTradovateStyle();
  const tradovateRef = useRef(tradovate);
  tradovateRef.current = tradovate;
  // Chart only (chartOnly.ts): the chart count drives its empty state; it changes when a module is
  // added or closed, never per tick.
  const chartOnly = useChartOnly();
  const [charts, setCharts] = useState<number | null>(null);

  const onReady = useCallback(
    (e: DockviewReadyEvent) => {
      const api = e.api;
      apiRef.current = api;
      const w = activeWorkspace(store.doc);
      let restored = false;
      if (w.layout) {
        try {
          // Migration on read (`migrateLayoutOnRead`): a saved Settings tab is dropped (Settings is
          // a dialog now), a repeated Accounts tab leaves, pop-outs come back floating.
          // The next capture below stores the cleaned layout.
          api.fromJSON(migrateLayoutOnRead(w.layout) as unknown as SerializedDockview);
          // An empty saved layout stays empty (the user closed everything).
          restored = true;
        } catch {
          restored = false;
        }
      }
      if (!restored) {
        const t = templateOf(w.template);
        if (t) applyTemplateTo(api, t);
      }
      api.updateOptions({ locked: w.locked, disableDnd: w.locked });
      // Dockview fits a restored layout to this window. Keep that exact live baseline locally:
      // saving the new pixels immediately would make differently sized windows echo forever.
      // Detach nested params: toJSON may retain objects that a panel later updates in place.
      let previous: SerializedLayout | undefined;
      const capture = (restoredFrom?: SerializedLayout): void => {
        // Events from a dock that is being replaced (workspace switch) are ignored, and so is
        // everything while Chart only is applied: its hidden modules are not a layout to save.
        if (apiRef.current !== api || store.doc.activeId !== w.id || chartOnlyApplied(api)) return;
        const next = JSON.parse(JSON.stringify(api.toJSON())) as SerializedLayout;
        store.captureLayout(w.id, next, previous ? { previous } : restoredFrom ? { restoredFrom } : {});
        previous = next;
      };
      api.onDidLayoutChange(() => {
        capture();
      });
      capture(restored && w.layout ? w.layout : undefined);
      // Chart only (after the capture above: the saved layout is the full one).
      const count = (): void => {
        if (apiRef.current === api) setCharts(dockChartCount(api));
      };
      api.onDidAddPanel((p) => {
        // Anything else opened while Chart only is on (e.g. Undo of a closed module) shows: leave it.
        if (chartOnlyApplied(api) && (p.params as WidgetParams | undefined)?.kind !== "chart")
          setChartOnly(false);
        count();
      });
      api.onDidRemovePanel(count);
      setChartOnlyCapture(api, capture);
      syncChartOnly(api);
      count();
      if (import.meta.env.DEV) {
        // e2e/chart-only-preview.mjs compares the live layout before / after (read-only).
        (window as unknown as { __helmDockLayout?: () => unknown }).__helmDockLayout = () =>
          apiRef.current === api ? api.toJSON() : null;
      }
      const headers = (): void => {
        if (apiRef.current === api) syncChartHeaders(api, tradovateRef.current);
      };
      api.onDidLayoutChange(headers);
      api.onDidAddGroup(headers);
      api.onDidMovePanel(headers);
      headers();
      api.onDidAddPopoutGroup((p) => {
        mirrorTheme(p.window);
        const off = onPopoutWindow?.(p.window);
        if (off) popoutCleanups.current.set(p.window, off);
      });
      api.onDidRemovePopoutGroup((p) => {
        popoutCleanups.current.get(p.window)?.();
        popoutCleanups.current.delete(p.window);
      });
      onApi(api);
    },
    [store, onApi, onPopoutWindow],
  );

  useEffect(() => {
    if (apiRef.current) syncChartHeaders(apiRef.current, tradovate);
  }, [tradovate]);

  // Switching Chart only applies to the dock at once (no React work in between).
  useEffect(
    () =>
      onChartOnlyChange(() => {
        if (apiRef.current) syncChartOnly(apiRef.current);
      }),
    [],
  );

  // Lock layout: no drags, drops or sash resizing (panels stay usable).
  useEffect(() => {
    apiRef.current?.updateOptions({ locked: ws.locked, disableDnd: ws.locked });
  }, [ws.locked]);

  // Keep pop-out windows on the current theme / side palette.
  useEffect(() => {
    const obs = new MutationObserver(() => {
      for (const p of apiRef.current?.getPopouts() ?? []) mirrorTheme(p.window);
    });
    obs.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme", "data-style", "style"],
    });
    const cleanups = popoutCleanups.current;
    return () => {
      obs.disconnect();
      for (const off of cleanups.values()) off();
      cleanups.clear();
    };
  }, []);

  useEffect(
    () => () => {
      apiRef.current = null;
      onApi(null);
    },
    [onApi],
  );

  const chrome: Chrome = {
    locked: ws.locked,
    close: (api, panel) => {
      const wsId = store.doc.activeId;
      closeWithUndo(api, panel, () => apiRef.current === api && store.doc.activeId === wsId);
    },
  };

  const wsId = ws.id;
  return (
    <ChromeContext.Provider value={chrome}>
      <DockviewReact
        key={`${wsId}:${String(revision)}`}
        className={ws.locked ? "dockview-theme-helm dock-locked" : "dockview-theme-helm"}
        components={{ widget: PanelFrame }}
        defaultTabComponent={ModuleTab}
        rightHeaderActionsComponent={GroupActions}
        watermarkComponent={EmptyWorkspace}
        popoutUrl="/popout.html"
        floatingGroupBounds="boundedWithinViewport"
        getTabContextMenuItems={({ panel, api }) => {
          const k = kindOf(panel);
          const locked = activeWorkspace(store.doc).locked;
          const where = panel.api.location.type;
          return [
            {
              label: "Close",
              disabled: locked,
              action: () => {
                chrome.close(api, panel);
              },
            },
            {
              label: "Close others in this group",
              disabled: locked || panel.group.panels.length < 2,
              action: () => {
                for (const p of panel.group.panels.filter((x) => x !== panel)) p.api.close();
              },
            },
            "separator",
            {
              label: "Maximize",
              disabled: where !== "grid",
              action: () => {
                panel.group.api.maximize();
              },
            },
            {
              label: "Float",
              disabled: locked || where !== "grid",
              action: () => {
                api.addFloatingGroup(panel);
              },
            },
            {
              label: "Open in new window",
              disabled: where === "popout",
              action: () => {
                void api.addPopoutGroup(panel);
              },
            },
            "separator",
            {
              label: `Duplicate ${k ? WIDGET_TITLES[k] : "module"}`,
              disabled: k === undefined || MODULE_BY_KIND.get(k)?.single === true,
              action: () => {
                workspaceActions.duplicate(api, panel);
              },
            },
          ];
        }}
        onReady={onReady}
      />
      {chartOnly && charts === 0 && (
        <ChartOnlyEmpty
          onAdd={() => {
            if (apiRef.current) workspaceActions.addModule(apiRef.current, "chart");
          }}
        />
      )}
    </ChromeContext.Provider>
  );
}
